// OperatorMilestoneModal — fires once when a user crosses into the
// `operator` tier. This is the strongest squad-pull moment in the journey:
// they've proven commitment solo, and the next stage of mastery is
// collaboration. We celebrate, then point them at /squads.
//
// Trigger logic lives here (not in a global provider) because the modal is
// the only consumer of the signal and we want it tree-shakable on the
// pages that don't mount it. Mounted globally in App.tsx.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Sparkles, X } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useXP, TIER_ORDER } from "@/hooks/useXP";
import { useMySquads } from "@/hooks/useSquads";

const STORAGE_KEY = "hl_operator_milestone_shown";

const OperatorMilestoneModal = () => {
  const { user } = useAuth();
  const { xp } = useXP();
  const { data: squads } = useMySquads();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user || !xp) return;
    // Only fire if the user has actually reached operator OR higher.
    const idx = TIER_ORDER.indexOf(xp.tier as (typeof TIER_ORDER)[number]);
    if (idx < TIER_ORDER.indexOf("operator")) return;
    // Don't nag people who already joined a squad.
    if (squads && squads.length > 0) return;
    // Show once per user per device.
    const shownKey = `${STORAGE_KEY}:${user.id}`;
    try {
      if (localStorage.getItem(shownKey) === "1") return;
      localStorage.setItem(shownKey, "1");
    } catch {
      /* ignore */
    }
    // Tiny delay so it doesn't slam in on first paint.
    const t = setTimeout(() => setOpen(true), 800);
    return () => clearTimeout(t);
  }, [user, xp, squads]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="operator-milestone-title"
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm animate-in fade-in"
      onClick={() => setOpen(false)}
    >
      <div
        className="relative w-full max-w-md rounded-xl border border-primary/40 bg-card p-6 shadow-2xl"
        style={{ boxShadow: "0 0 60px hsl(var(--primary) / 0.25)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={() => setOpen(false)}
          aria-label="Close"
          className="absolute top-3 right-3 text-muted-foreground hover:text-foreground"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full border border-primary/40 bg-primary/10 text-primary text-[10px] font-mono uppercase tracking-wider mb-4">
          <Sparkles className="w-3 h-3" /> Tier unlocked
        </div>

        <h2
          id="operator-milestone-title"
          className="text-2xl font-mono font-extrabold text-foreground mb-2"
        >
          You're an <span className="text-primary text-glow">Operator</span>.
        </h2>
        <p className="text-sm font-mono text-muted-foreground mb-5">
          You've outgrown solo. The next stage of mastery is collaboration —
          squads ship 3× faster, swap playbooks, and keep each other moving.
        </p>

        <div className="flex flex-col gap-2">
          <Link
            to="/squads"
            onClick={() => setOpen(false)}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-primary text-primary-foreground text-sm font-mono font-semibold hover:bg-primary/90 transition-colors"
          >
            <Users className="w-4 h-4" /> Find your squad
          </Link>
          <button
            onClick={() => setOpen(false)}
            className="text-xs font-mono text-muted-foreground hover:text-foreground py-1"
          >
            Not yet — keep building solo
          </button>
        </div>
      </div>
    </div>
  );
};

export default OperatorMilestoneModal;
